import React, { useEffect, useRef, useMemo } from 'react';
import * as d3 from 'd3';
import Legend, { GScaleLegend } from '../components/Legend';
import { selectDstGuideLevels } from '../utils/dstGScale';
import {
  formatLog10Tick,
  log10DecadeTicks,
  buildXrayRScaleBands,
} from '../utils/xrayFlareScale';

// Fits in existing left margin — do not widen container / margins.
const SCALE_BAR_WIDTH = 10;
const SCALE_BAR_GAP = 2;

const MultiLine = ({
  data = [],
  title = '',
  xAxisLabel = '',
  yAxisLabel = '',
  xDomain,                 // optional fixed [startDate, endDate]
  yDomain,                 // optional fixed [min,max]
  yScaleType = 'linear',   // 'linear' | 'log'
  classLabels = [],        // optional [{ label, value }] for log-decade class marks (e.g. A/B/C/M/X)
  scaleBarLevels = [],     // optional NOAA R-scale bands [{ label, value, minBound, maxBound, color }]
  guideLines = [],         // optional [{ level, label, value, color }]
  lines = [],              // array of line configurations: [{key, color, label, valueAccessor}]
  startDate,               // kept for compatibility (unused here)
  endDate,                 // kept for compatibility (unused here)
  zoomTransform = d3.zoomIdentity, // shared from parent
  onZoom,                  // (transform) => void
  onShiftTimeline,         // kept for compatibility (unused here)
  dragShift,               // kept for compatibility (unused here)
  onHover,                 // ({date,time,value}) | null
}) => {
  const svgRef = useRef(null);
    const containerRef = useRef(null);
    const clipIdRef = useRef(`clip-${Math.random().toString(36).slice(2)}`);
     // Outer size + margins			  // Get container dimensions
    const [dimensions, setDimensions] = React.useState({ width: 1000, height: 120 });
    React.useEffect(() => {
      const updateDimensions = () => {
        if (containerRef.current) {
          const rect = containerRef.current.getBoundingClientRect();
          setDimensions({
            width: Math.max(1, rect.width - 20),
            height: Math.max(1, rect.height - 20)
          });
        }
      };
       updateDimensions();
      window.addEventListener('resize', updateDimensions);
      return () => window.removeEventListener('resize', updateDimensions);
    }, []);
    const { width, height } = dimensions;
    const margin = { top: 30, right: 40, bottom: 20, left: 50 };
  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;

  // Base X scale (un-zoomed), in inner coordinates [0..innerWidth]
  const x = useMemo(() => {
    const hasExplicitDomain =
      Array.isArray(xDomain) &&
      xDomain.length === 2 &&
      xDomain[0] != null &&
      xDomain[1] != null;

    const domain = hasExplicitDomain
      ? [new Date(xDomain[0]), new Date(xDomain[1])]
      : data.length
        ? d3.extent(data, (d) => new Date(d.date))
        : [new Date(), new Date()];

    return d3.scaleUtc().domain(domain).range([0, innerWidth]);
  }, [data, innerWidth, xDomain]);

  // Y scale (inner coordinates [innerHeight..0])
  const y = useMemo(() => {
    const useLog = yScaleType === 'log';

    if (Array.isArray(yDomain) && yDomain.length === 2) {
      const domain = useLog
        ? [Math.max(yDomain[0], Number.MIN_VALUE), Math.max(yDomain[1], Number.MIN_VALUE)]
        : yDomain;
      const scale = useLog
        ? d3.scaleLog().base(10).domain(domain).range([innerHeight, 0])
        : d3.scaleLinear().domain(domain).range([innerHeight, 0]);
      return scale.clamp(true);
    }

    let min = Infinity;
    let max = -Infinity;

    lines.forEach((line) => {
      const values = data
        .map((d) => line.valueAccessor(d))
        .filter((v) => v !== null && !isNaN(v) && (!useLog || v > 0));
      if (values.length > 0) {
        min = Math.min(min, d3.min(values));
        max = Math.max(max, d3.max(values));
      }
    });

    if (!Number.isFinite(min) || !Number.isFinite(max) || min === max) {
      min = useLog ? 1e-9 : 0;
      max = useLog ? 1e-2 : 1;
    }

    if (useLog) {
      const padMin = Math.max(min / 10, Number.MIN_VALUE);
      const padMax = max * 10;
      return d3.scaleLog().base(10).domain([padMin, padMax]).range([innerHeight, 0]).nice();
    }
    return d3.scaleLinear().domain([min, max]).range([innerHeight, 0]).nice();
  }, [data, lines, yDomain, yScaleType, innerHeight]);

  const activeGuides = useMemo(
    () => (guideLines.length ? selectDstGuideLevels(guideLines, y, innerHeight) : []),
    [guideLines, y, innerHeight]
  );

  const showScaleBar = scaleBarLevels.length > 0;
  const sideBarBands = useMemo(
    () => (showScaleBar ? buildXrayRScaleBands(scaleBarLevels, y.domain()) : []),
    [showScaleBar, scaleBarLevels, y]
  );

  useEffect(() => {
    const root = d3.select(svgRef.current);
    root.selectAll('*').remove();

    // --- Root SVG + inner container -------------------------------------
    root.attr('width', width).attr('height', height);

    const container = root
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // --- Titles/labels (draw on root, not inside inner coords) ----------
     // --- Titles/labels (draw on root, not inside inner coords) ----------
    root
     .append('text')
     .attr('x', margin.left / 2) // Position near the left margin
     .attr('y', 10) // Position close to the top of the SVG
     .attr('text-anchor', 'start') // Align text to the start (left)
     .style('font-size', '14px') // Slightly larger font for the title
     .style('font-weight', 'bold')
     .text(title);

   // y Axis label
    root
     .append('text')
     .attr('transform', 'rotate(-90)')
     .attr('x', -height / 2)
     .attr('y', 16)
     .attr('text-anchor', 'middle')
     .style('font-size', '12px')
     .text(yAxisLabel);

    // --- Clip path for inner plotting region ----------------------------
    container
      .append('defs')
      .append('clipPath')
      .attr('id', clipIdRef.current)
      .append('rect')
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', innerWidth)
      .attr('height', innerHeight);

    // --- Axes groups -----------------------------------------------------
    const xAxisG = container
      .append('g')
      .attr('class', 'x-axis')
      .attr('transform', `translate(0,${innerHeight})`);

    const yAxisG = container.append('g').attr('class', 'y-axis');

    // Build X axis factory so tick format follows zoomed domain
    const makeXAxis = (xs) =>
      d3
        .axisBottom(xs)
        .ticks(Math.max(2, Math.floor(innerWidth / 200)))
        .tickFormat((d) => {
          const [s, e] = xs.domain();
          const visibleDays = (e - s) / 86400000; // ms -> days
          if (visibleDays <= 20) return d3.timeFormat('%H:%M')(d);
          const q = Math.floor(d.getMonth() / 3) + 1;
          return `Q${q} ${d.getFullYear()}`;
        });

    // Y axis + grid cloned from ticks (log: decade ticks only — minor log
    // ticks would each spawn a full-width dashed gridline and fill the plot)
    const drawYAxis = (ys) => {
      const isLog = yScaleType === 'log';
      let axis;
      if (isLog) {
        const [yMin, yMax] = ys.domain();
        axis = d3
          .axisRight(ys)
          .tickValues(log10DecadeTicks(yMin, yMax))
          .tickFormat(formatLog10Tick)
          .tickSizeOuter(0);
      } else {
        axis = d3.axisRight(ys).ticks(5).tickSizeOuter(0);
      }

      yAxisG
        .attr('transform', `translate(${innerWidth},0)`)
        .call(axis)
        .call((g) => g.select('.domain').remove())
        .call((g) =>
          g
            .selectAll('.tick line')
            .clone()
            .attr('x1', -innerWidth)
            .attr('stroke-opacity', 0.2)
            .attr('stroke-dasharray', '2,2')
        );
    };

    // --- G-scale horizontal guide lines (full width) ---------------------
    const guideG = container
      .append('g')
      .attr('class', 'guide-lines')
      .attr('clip-path', `url(#${clipIdRef.current})`);

    const drawGuides = () => {
      guideG.selectAll('*').remove();
      activeGuides.forEach((guide) => {
        const yPos = y(guide.value);

        guideG
          .append('line')
          .attr('x1', 0)
          .attr('x2', innerWidth)
          .attr('y1', yPos)
          .attr('y2', yPos)
          .attr('stroke', guide.color)
          .attr('stroke-width', 3)
          .attr('stroke-opacity', 0.95);

        guideG
          .append('text')
          .attr('x', innerWidth - 4)
          .attr('y', yPos - 4)
          .attr('text-anchor', 'end')
          .style('font-size', '10px')
          .style('font-weight', 'bold')
          .style('fill', guide.color)
          .text(guide.label);
      });
    };

    drawGuides();

    // --- NOAA R-scale color bar (left margin; margins unchanged) ----------
    const scaleBarG = container.append('g').attr('class', 'r-scale-bar');
    const drawScaleBar = () => {
      scaleBarG.selectAll('*').remove();
      if (!showScaleBar) return;

      // Sit in existing left margin, flush to plot edge — no margin/container change.
      const barX = -(SCALE_BAR_WIDTH + SCALE_BAR_GAP);
      sideBarBands.forEach((band) => {
        const yTop = y(band.hi);
        const yBot = y(band.lo);
        const h = Math.max(0, yBot - yTop);

        scaleBarG
          .append('rect')
          .attr('x', barX)
          .attr('y', yTop)
          .attr('width', SCALE_BAR_WIDTH)
          .attr('height', h)
          .attr('fill', band.color)
          .attr('stroke', '#fff')
          .attr('stroke-width', 0.5);

        if (band.label && h >= 8) {
          scaleBarG
            .append('text')
            .attr('x', 3)
            .attr('y', (yTop + yBot) / 2)
            .attr('dy', '0.35em')
            .attr('text-anchor', 'start')
            .style('font-size', '9px')
            .style('font-weight', 'bold')
            .style('fill', band.color)
            .text(band.label);
        }
      });

      if (sideBarBands.length) {
        scaleBarG
          .append('text')
          .attr('x', barX + SCALE_BAR_WIDTH / 2)
          .attr('y', -6)
          .attr('text-anchor', 'middle')
          .style('font-size', '8px')
          .style('fill', '#555')
          .text('R');
      }
    };
    drawScaleBar();

    // --- Flare class labels (A/B/C/M/X) at log₁₀ decades -------------------
    const classG = container.append('g').attr('class', 'flare-class-labels');
    const drawClassLabels = () => {
      classG.selectAll('*').remove();
      classLabels.forEach((cls) => {
        if (!(cls.value > 0)) return;
        const yPos = y(cls.value);
        if (yPos < 0 || yPos > innerHeight) return;

        classG
          .append('line')
          .attr('x1', 0)
          .attr('x2', innerWidth)
          .attr('y1', yPos)
          .attr('y2', yPos)
          .attr('stroke', '#888')
          .attr('stroke-width', 1)
          .attr('stroke-dasharray', '3,3')
          .attr('stroke-opacity', 0.45);

        classG
          .append('text')
          .attr('x', showScaleBar ? 26 : 4)
          .attr('y', yPos - 3)
          .attr('text-anchor', 'start')
          .style('font-size', '11px')
          .style('font-weight', 'bold')
          .style('fill', '#444')
          .text(cls.label);
      });
    };
    drawClassLabels();

    const isValidY = (v) =>
      v !== null && !isNaN(v) && (yScaleType !== 'log' || v > 0);

    // --- Paths for each line ---------------------------------------------
    lines.forEach((line, index) => {
      const lineGen = d3.line()
        .defined((d) => isValidY(line.valueAccessor(d)))
        .x((d) => x(new Date(d.date)))
        .y((d) => y(line.valueAccessor(d)));

      // Line path
      container
        .append('path')
        .datum(data)
        .attr('clip-path', `url(#${clipIdRef.current})`)
        .attr('fill', 'none')
        .attr('stroke', line.color)
        .attr('stroke-width', 2)
        .attr('d', lineGen)
        .attr('class', `line-${index}`);
    });

    // --- Day/Month separators -------------------------------------------
    const sepG = container.append('g').attr('class', 'day-separators');
    const labelG = container.append('g').attr('class', 'day-labels');

    const drawSeparators = (xs) => {
      sepG.selectAll('*').remove();
      labelG.selectAll('*').remove();

      const [s, e] = xs.domain();
      const visibleDays = (e - s) / 86400000;

      if (visibleDays <= 20) {
        const days = d3.timeDay.range(s, e);
        sepG
          .selectAll('line')
          .data(days)
          .join('line')
          .attr('x1', (d) => xs(d))
          .attr('x2', (d) => xs(d))
          .attr('y1', 0)
          .attr('y2', innerHeight)
          .attr('stroke', '#ccc')
          .attr('stroke-width', 1)
          .attr('stroke-dasharray', '4,4');

        labelG
          .selectAll('text')
          .data(days)
          .join('text')
          .attr('x', (d) => xs(d) + 5)
          .attr('y', -10)
          .attr('text-anchor', 'start')
          .style('font-size', '12px')
          .style('font-family', "'Roboto', sans-serif")
          .style('fill', '#666')
          .text((d) => d3.timeFormat('%b %d')(d));
      } else {
        const months = d3.timeMonth.range(s, e);
        sepG
          .selectAll('line')
          .data(months)
          .join('line')
          .attr('x1', (d) => xs(d))
          .attr('x2', (d) => xs(d))
          .attr('y1', 0)
          .attr('y2', innerHeight)
          .attr('stroke', '#999')
          .attr('stroke-width', 1.5)
          .attr('stroke-dasharray', '4,4');

        labelG
          .selectAll('text')
          .data(months)
          .join('text')
          .attr('x', (d) => xs(d) + 5)
          .attr('y', -10)
          .attr('text-anchor', 'start')
          .style('font-size', '12px')
          .style('font-family', "'Roboto', sans-serif")
          .style('fill', '#333')
          .text((d) => d3.timeFormat('%b %Y')(d));
      }
    };

    // Initial axes + separators
    xAxisG.call(makeXAxis(x));
    drawYAxis(y);
    drawSeparators(x);

    // --- Zoom/Pan (single source of truth for pan) -----------------------
    const updateWithTransform = (t) => {
      const zx = t.rescaleX(x);

      // update axes
      xAxisG.call(makeXAxis(zx));

      // update paths for each line
      lines.forEach((line, index) => {
        const lineGen = d3
          .line()
          .defined((d) => isValidY(line.valueAccessor(d)))
          .x((d) => zx(new Date(d.date)))
          .y((d) => y(line.valueAccessor(d)))
          .curve(d3.curveMonotoneX);

        root.select(`.line-${index}`).attr('d', lineGen(data));
      });

      // update separators
      drawSeparators(zx);
      drawGuides();
      drawClassLabels();

      return zx;
    };

    const zoom = d3
      .zoom()
      .scaleExtent([1, 20])
      .translateExtent([
        [0, 0],
        [innerWidth, innerHeight],
      ])
      .on('zoom', (event) => {
        const t = event.transform;
        updateWithTransform(t);
        if (onZoom) onZoom(t);
      });

    // Transparent overlay to capture pointer interactions
    const overlay = container
      .append('rect')
      .attr('class', 'overlay')
      .attr('width', innerWidth)
      .attr('height', innerHeight)
      .attr('fill', 'none')
      .attr('pointer-events', 'all');

    // Attach zoom to the root SVG so wheel/drag works across the plot
    root.call(zoom);

    // Apply initial shared transform from parent
    root.call(zoom.transform, zoomTransform);
    let currentZX = updateWithTransform(zoomTransform);

    // --- Hover that respects zoom ----------------------------------------
    overlay
      .on('mousemove', (event) => {
        const [mx] = d3.pointer(event, overlay.node());
        const t = d3.zoomTransform(root.node());
        currentZX = t.rescaleX(x);

        const hoveredDate = currentZX.invert(mx);
        const bisect = d3.bisector((d) => new Date(d.date)).left;
        const i = bisect(data, hoveredDate);
        const d0 = data[i - 1];
        const d1 = data[i];

        let closest = d0 ?? d1 ?? null;
        if (d0 && d1) {
          closest =
            hoveredDate - new Date(d0.date) >
            new Date(d1.date) - hoveredDate
              ? d1
              : d0;
        }

        if (closest && onHover) {
          const dt = new Date(closest.date);
          if (!isNaN(dt)) {
            // Find the closest line to the mouse
            let closestLine = null;
            let minDistance = Infinity;
            
            lines.forEach(line => {
              const value = line.valueAccessor(closest);
              if (isValidY(value)) {
                const yPos = y(value);
                const distance = Math.abs(mx - yPos);
                if (distance < minDistance) {
                  minDistance = distance;
                  closestLine = line;
                }
              }
            });
            
            if (closestLine) {
              onHover({ 
                date: dt, 
                time: dt, 
                value: closestLine.valueAccessor(closest),
                lineLabel: closestLine.label 
              });
            }
          }
        }
      })
      .on('mouseleave', () => {
        if (onHover) onHover(null);
      });

    // Cleanup zoom listeners on unmount
    return () => {
      root.on('.zoom', null);
    };
  }, [
    data,
    x,
    y,
    width,
    height,
    innerWidth,
    innerHeight,
    margin.left,
    margin.top,
    lines,
    yDomain,
    yScaleType,
    xDomain,
    classLabels,
    scaleBarLevels,
    showScaleBar,
    sideBarBands,
    guideLines,
    activeGuides,
    zoomTransform,
    onZoom,
    onHover,
    dimensions,
  ]);

  return (
    <div
      ref={containerRef}
      className="chart-wrapper"
      style={{ position: 'relative', width: '100%', height: '100%' }}
    >
      <svg ref={svgRef} />
      {/* Legend overlay in the top-left corner */}
      <div style={{ position: 'absolute', top: 22, left: 35, zIndex: 10 }}>
        {lines.map((line, index) => (
          <div key={line.key ?? index} style={{ marginBottom: '2px' }}>
            <Legend
              lineColor={line.color}
              label={line.label}
              onClose={() => {
                console.log(`Legend closed for ${line.label}`);
              }}
            />
          </div>
        ))}
        {activeGuides.length > 0 && (
          <div style={{ marginBottom: '2px' }}>
            <GScaleLegend guides={activeGuides} />
          </div>
        )}
      </div>
    </div>
  );
};

// Prevent unnecessary re-renders
export default React.memo(MultiLine, (prev, next) => {
  return (
    prev.data === next.data &&
    prev.lines === next.lines &&
    prev.guideLines === next.guideLines &&
    prev.classLabels === next.classLabels &&
    prev.scaleBarLevels === next.scaleBarLevels &&
    prev.yDomain === next.yDomain &&
    prev.yScaleType === next.yScaleType &&
    prev.zoomTransform === next.zoomTransform
  );
});
